import { IonRouterLink } from '@ionic/react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';

import ActionIconButton from '@components/buttons/ActionIconButton';
import BasicCard from '@components/cards/BasicCard';
import styles from '@components/carePlan/CarePlan.module.css';
import PinToggle from '@components/PinToggle';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import useMyExpert from '@hooks/useMyExpert';
import { Opener } from '@hooks/useOpener';
import {
  CarePlanActionItemCtaEnum,
  GetJourneyWithPlanQuery,
  GoalTemplateFragment,
  PinResourceTypeEnum
} from '@typing/Generated';
import { Status } from '@typing/Models';
import { journeyRoute } from '@utils/routeUtils';

type Journey = GetJourneyWithPlanQuery['journey'];

type Worksheet = Journey['worksheets'][0];
type WorksheetTemplate = GoalTemplateFragment['worksheetTemplates'][0];

export type ToolType = {
  status: Status;
  type?: CarePlanActionItemCtaEnum;
  worksheet?: Worksheet;
  worksheetTemplate?: WorksheetTemplate;
};

type ToolProps = {
  archived: boolean;
  handleRemove?: (tool: ToolType) => void;
  journeyId: string;
  legalOpener: Opener;
  medicationTrackerOpener: Opener;
  providerOpener: Opener;
  tool: ToolType;
};

const Tool = ({
  archived,
  handleRemove,
  journeyId,
  legalOpener,
  medicationTrackerOpener,
  providerOpener,
  tool
}: ToolProps) => {
  const history = useHistory();
  const myExpert = useMyExpert();

  const handleToolClick = (tool: ToolType) => {
    if (tool.worksheet) {
      history.push(
        journeyRoute({
          journeyId,
          linkAfterJourney: `/worksheets/${tool.worksheet.id}`,
          myExpert
        })
      );
    }
    if (tool.worksheetTemplate) {
      history.push(
        journeyRoute({
          journeyId,
          linkAfterJourney: `/worksheet-templates/${tool.worksheetTemplate.id}`,
          myExpert
        })
      );
    }

    if (tool.type === CarePlanActionItemCtaEnum.complete_legal_document_tracker) {
      legalOpener.open();
    }
    if (tool.type === CarePlanActionItemCtaEnum.complete_provider_list) {
      providerOpener.open();
    }
    if (tool.type === CarePlanActionItemCtaEnum.complete_medication_tracker) {
      medicationTrackerOpener.open();
    }
  };

  return (
    <IonRouterLink className={styles.toolWrapper} onClick={() => handleToolClick(tool)}>
      <BasicCard hover="purple" inactive={archived} padding="normal">
        <div className={styles.tool}>
          <div className={styles.textRight}>
            {!tool.worksheetTemplate && (
              <PinToggle
                color="neutral500"
                journeyId={journeyId}
                resourceId={tool.worksheet?.id}
                resourceType={tool.type ? undefined : PinResourceTypeEnum.WORKSHEET}
                tool={tool.type}
              />
            )}

            {myExpert && handleRemove && (
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.delete"
                color="neutral200"
                icon="times"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemove(tool);
                }}
              />
            )}
          </div>
          <div className={`${archived ? styles.circleLinesArchived : styles.circleLines}`}>
            <div className={styles.circleLine}>
              <div className={styles.circle} />
              <div className={styles.line} />
            </div>
            <div className={styles.circleLine}>
              <div className={styles.circle} />
              <div className={styles.line} />
            </div>
            <div className={styles.circleLine}>
              <div className={styles.circle} />
              <div className={styles.line} />
            </div>
          </div>
          <div>
            <BodyCopyBold color="neutral500">
              {tool.worksheet ? (
                tool.worksheet.title
              ) : tool.worksheetTemplate ? (
                tool.worksheetTemplate.title
              ) : (
                <FormattedMessage id={`models.goalTemplate.tools.${tool.type ?? ''}`} />
              )}
            </BodyCopyBold>
          </div>
        </div>
      </BasicCard>
    </IonRouterLink>
  );
};

export default Tool;
